import {FormEventHandler, forwardRef, ReactNode, useEffect, useMemo} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import MobxSelect from 'components/elements/fields/MobxSelect'
import {
	autoCalculatorOptions,
	employmentTypeOptions,
	oftenPaidOptions,
	paymentTypeOptions,
	payStubNumberOptions,
	TYesNo,
	yesNoOptions,
} from 'models/PayStub/PayStub.configs'
import {getOftenPaidNumberValue} from 'src/utils'
import {usePayStubStore} from 'src/utils/contexts/RootStore.context'

import s from './FieldsPayStubStart.module.scss'

const cx = cn.bind(s)

interface IFieldsPayStubStartProps {
	className?: string
	children?: ReactNode | string
	onChange?: FormEventHandler<HTMLDivElement>
}

const FieldsPayStubStart = forwardRef<HTMLDivElement, IFieldsPayStubStartProps>(
	({className, children, ...props}, ref) => {
		const payStubStore = usePayStubStore()
		const {
			employmentType,
			paymentType,
			oftenPaid,
			isHiredInPast52Weeks,
			isPrevYtd,
			payPeriodsWorked,
		} = payStubStore

		/** If employment type is "contractor" the payment type can have only one options - "hourly" */
		const filteredPaymentOptions = useMemo(() => {
			if (employmentType === 'contractor') {
				return paymentTypeOptions.filter((item) => item.value === 'hourly')
			}
			return paymentTypeOptions
		}, [employmentType])

		/** If payment type is "salary" the employment type can have only one options - "employee" */
		const filteredEmploymentOptions = useMemo(() => {
			if (paymentType === 'salary') {
				return employmentTypeOptions.filter((item) => item.value === 'employee')
			}
			return employmentTypeOptions
		}, [paymentType])

		useEffect(() => {
			if (isHiredInPast52Weeks) {
				const maxPeriodWorked = Math.min(Number(payPeriodsWorked), 5)

				if (maxPeriodWorked < Number(payStubStore.payStubsNumber)) {
					payStubStore.setPayStubNumber(maxPeriodWorked.toString())
				}
			}
		}, [isHiredInPast52Weeks, payPeriodsWorked, payStubStore])

		/** Paystub number options will change if isHiredInPast52Weeks is "true" */
		const filterPayStubNumberOptions = useMemo(() => {
			if (isHiredInPast52Weeks) {
				/** A limit on the number of possible document creations just to reduce the number of Symmetry requests. */
				const maxPeriodWorked = Math.min(Number(payPeriodsWorked), 5)

				return Array.from(Array(Number(maxPeriodWorked)).keys()).map((item) => {
					return {
						name: String(item + 1),
						value: item + 1,
					}
				})
			} else {
				const oftenPaidNumber = getOftenPaidNumberValue(oftenPaid)
				/** A limit on the number of possible document creations just to reduce the number of Symmetry requests. */
				const maxOftenPaidPeriod = Math.min(oftenPaidNumber, 5)
				return [...payStubNumberOptions.slice(0, maxOftenPaidPeriod)]
			}
		}, [isHiredInPast52Weeks, oftenPaid, payPeriodsWorked])

		/** Creates pay periods worked options for selector. */
		const payPeriodsWorkedOptions = useMemo(() => {
			const oftenPaidNumber = getOftenPaidNumberValue(oftenPaid)
			/** A limit on the number of possible document creations just to reduce the number of Symmetry requests. */
			const maxOftenPaidNumber = Math.min(Number(oftenPaidNumber), 5)
			return Array.from(Array(Number(maxOftenPaidNumber)).keys()).map((item) => {
				return {
					name: String(item + 1),
					value: item + 1,
				}
			})
		}, [oftenPaid])

		return (
			<div className={cx('start', className)} {...props} ref={ref}>
				{children}
				<div className={cx('start--fields')}>
					<MobxSelect
						name="autoCalculator"
						label="Auto Calculator"
						store={payStubStore}
						wrapperClassName={cx('field')}
						options={autoCalculatorOptions}
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
					/>
					<MobxSelect
						name="employmentType"
						label="Employment type"
						store={payStubStore}
						wrapperClassName={cx('field')}
						options={filteredEmploymentOptions}
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
					/>
					<MobxSelect
						name="paymentType"
						label="Payment type"
						store={payStubStore}
						wrapperClassName={cx('field')}
						options={filteredPaymentOptions}
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
					/>
					<MobxSelect
						name="oftenPaid"
						label="How often are you paid?"
						store={payStubStore}
						wrapperClassName={cx('field')}
						options={oftenPaidOptions}
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
					/>
					{!isHiredInPast52Weeks && (
						<MobxSelect
							name="prevYtd"
							label="Enter in previous YTD calculations?"
							store={payStubStore}
							wrapperClassName={cx('field')}
							options={yesNoOptions}
							onChange={(e) => {
								payStubStore.prevYtdItem?.setEarningsDefaultPrevYtd()
								payStubStore.prevYtd = e.target.value as TYesNo
								payStubStore.prevYtdItem?.calculate()
							}}
						/>
					)}

					{!isPrevYtd && (
						<MobxSelect
							name="hiredInPast52Weeks"
							label="Were you hired in the past 52 weeks?"
							store={payStubStore}
							wrapperClassName={cx('field')}
							options={yesNoOptions}
						/>
					)}
					{isHiredInPast52Weeks && (
						<MobxSelect
							name="payPeriodsWorked"
							label="How many pay periods have you worked?"
							store={payStubStore}
							wrapperClassName={cx('field')}
							options={payPeriodsWorkedOptions}
							onChange={payStubStore.setPayPeriodsWorked}
						/>
					)}
					<MobxSelect
						name="payStubsNumber"
						label="Number of paystubs needed"
						store={payStubStore}
						data-testid="paystubs-number"
						wrapperClassName={cx('field')}
						options={filterPayStubNumberOptions}
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
						onChange={(e) => {
							payStubStore.setPayStubNumber(e.target.value)
						}}
					/>
				</div>
			</div>
		)
	},
)

FieldsPayStubStart.displayName = 'FieldsPayStubStart'

export default observer(FieldsPayStubStart)
